import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowLeft,
  GraduationCap,
  Building2,
  Users,
  Briefcase,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  BarChart,
  FileText,
  Quote,
  Zap,
  Clock,
  Rocket,
  BookOpen,
  Menu,
  Plus,
  MessageSquare,
  Sparkles,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { motion } from "framer-motion";

const EduConnectPage = ({ onBack }) => {
  const scrollToForm = () => {
    document
      .getElementById("request-demo")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const navigate = useHistory();

  const steps = [
    {
      step: "01",
      title: "College Registration",
      desc: "Register your institution and set up your placement cell profile in minutes.",
      colorStyle: styles.stepOrange,
    },
    {
      step: "02",
      title: "Student Onboarding",
      desc: "Bulk upload student data or invite them to create verified profiles.",
      colorStyle: styles.stepBlue,
    },
    {
      step: "03",
      title: "Connect with Companies",
      desc: "Gain visibility to 5,000+ companies hiring on Talent Connect.",
      colorStyle: styles.stepPurple,
    },
    {
      step: "04",
      title: "Track & Close Placements",
      desc: "Manage drives, track offers, and generate detailed placement reports.",
      colorStyle: styles.stepGreen,
    },
  ];

  const features = [
    {
      title: "300+ HR Contacts/Leads",
      desc: "Get fresh HR leads every month to boost your placement outreach.",
      icon: <Users style={{ color: "#FF5F14" }} />,
    },
    {
      title: "Company & Job Visibility",
      desc: "Showcase your college to top recruiters actively hiring freshers.",
      icon: <Building2 style={{ color: "#FF5F14" }} />,
    },
    {
      title: "Student Onboarding & Tracking",
      desc: "Seamlessly manage student profiles, resumes, and verification status.",
      icon: <BarChart style={{ color: "#FF5F14" }} />,
    },
    {
      title: "Job Offers Management",
      desc: "Centralized dashboard to track all job openings and student applications.",
      icon: <Briefcase style={{ color: "#FF5F14" }} />,
    },
    {
      title: "Analytics & Placement Reports",
      desc: "Generate comprehensive reports for NAAC/NBA accreditation.",
      icon: <FileText style={{ color: "#FF5F14" }} />,
    },
    {
      title: "Secure & Verified Access",
      desc: "Role-based access control for placement officers and student coordinators.",
      icon: <ShieldCheck style={{ color: "#FF5F14" }} />,
    },
  ];

  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);

  // Fetch testimonials from API
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setTestimonialsLoading(true);
        const response = await axios.get(
          "https://govtjobsapi.governmentjobsforgraduates.com/api/web/WebUser/get_testimonials",
        );
        if (response.data && response.data.data) {
          // Map API response to match component structure
          const mappedTestimonials = response.data.data.map((item) => ({
            id: item.id,
            quote: item.testimonial_text,
            author: item.reviewer_name,
            role: item.reviewer_position,
            college: item.college_name,
            rating: parseInt(item.rating) || 5,
            profile_picture: item.profile_picture,
          }));
          setTestimonials(mappedTestimonials);
        }
      } catch (error) {
        console.error("Error fetching testimonials:", error);
        setTestimonials([]);
      } finally {
        setTestimonialsLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  // Navigation handlers for testimonials
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [cardsPerView, setCardsPerView] = useState(3);

  useEffect(() => {
    const updateCardsPerView = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setCardsPerView(1);
      } else if (width < 992) {
        setCardsPerView(2);
      } else {
        setCardsPerView(3);
      }
    };

    updateCardsPerView();
    window.addEventListener("resize", updateCardsPerView);
    return () => window.removeEventListener("resize", updateCardsPerView);
  }, []);

  // Extended list: original + first `cardsPerView` items cloned at the end
  const extendedTestimonials = [
    ...testimonials,
    ...testimonials.slice(0, cardsPerView),
  ];

  useEffect(() => {
    if (testimonials.length <= cardsPerView) return;

    const interval = setInterval(() => {
      setTestimonialIndex((prev) => prev + 1);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  // When we've scrolled past the real items into the cloned ones, snap back invisibly
  useEffect(() => {
    if (testimonialIndex === testimonials.length) {
      const timeout = setTimeout(() => {
        setIsTransitioning(false); // disable animation for the snap
        setTestimonialIndex(0); // jump back to real index 0
      }, 600); // matches the transition duration below

      return () => clearTimeout(timeout);
    } else {
      setIsTransitioning(true); // re-enable animation for normal moves
    }
  }, [testimonialIndex, testimonials.length]);

  return (
    <div style={styles.page}>
      {/* Navigation Header */}
      {/* <div style={styles.navHeader}>
        <div className="container d-flex align-items-center justify-content-between h-100 px-4">
          <button onClick={() => navigate.goBack()} style={styles.backButton}>
            <ArrowLeft size={16} /> Back
          </button>
          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
            <GraduationCap size={24} color="#FF5F14" />
            <span style={styles.logoText}>
              EduConnect<span style={{ color: "#FF5F14" }}>Pro</span>
            </span>
          </div>
          <button
            onClick={scrollToForm}
            className="d-none d-md-flex align-items-center justify-content-center"
            style={styles.demoButtonSm}
          >
            Request Demo
          </button>
        </div>
      </div> */}

      {/* 1) HERO SECTION */}
      <div
        className="position-relative overflow-hidden bg-white"
        style={styles.heroSection}
      >
        <div style={styles.heroBgBlob}></div>

        <div
          className="container position-relative z-10"
          style={{ paddingTop: "4rem" }}
        >
          <div className="row align-items-center" style={{ gap: "3rem 0" }}>
            <div className="col-12 col-lg-6 text-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="d-inline-flex align-items-center"
                style={styles.heroEyebrow}
              >
                <span style={styles.heroEyebrowDot}></span>
                For Colleges & Institutions
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="heroTitle"
                style={styles.heroTitle}
              >
                Bridging Colleges, Companies <br />
                <span style={{ color: "#FF5F14" }}>& Careers Seamlessly</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="heroDesc"
                style={styles.heroDesc}
              >
                Empower your institution with EduConnectPro. Connect students,
                companies, and opportunities on a single unified platform.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="d-flex flex-column flex-sm-row"
                style={{ gap: "1rem" }}
              >
                <button
                  onClick={scrollToForm}
                  className="orangeBtn animateBtn"
                  style={styles.primaryButtonLg}
                >
                  Start Free Trial <ArrowRight size={20} />
                </button>
                <button
                  onClick={scrollToForm}
                  className="secondaryButton"
                  style={styles.secondaryButtonLg}
                >
                  Contact Sales
                </button>
              </motion.div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="d-flex  align-items-center mt-4"
                style={styles.heroTrust}
              >
                <CheckCircle2
                  size={16}
                  color="#22c55e"
                  style={{ marginRight: "8px" }}
                />{" "}
                14-Day Free Trial
                <span style={{ color: "#d1d5db", margin: "0 12px" }}>|</span>
                <CheckCircle2
                  size={16}
                  color="#22c55e"
                  style={{ marginRight: "8px" }}
                />{" "}
                No Credit Card Required
              </motion.p>
            </div>

            <div
              className="col-12 col-lg-6 d-flex align-items-center justify-content-center"
              style={{ minHeight: "500px" }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="position-relative d-flex align-items-center justify-content-center"
                style={styles.heroImageWrapper}
              >
                <div style={styles.heroImageCircle}></div>
                <div
                  className="text-center position-relative z-10"
                  style={{ padding: "2rem" }}
                >
                  <Briefcase
                    size={64}
                    color="#FF5F14"
                    style={{ margin: "0 auto 1rem" }}
                  />
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    300+ Leads
                  </h3>
                  <p style={{ color: "#6b7280", margin: 0 }}>
                    Delivered Monthly
                  </p>
                </div>

                <div style={styles.floatingBadge1}>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "12px" }}
                  >
                    <div style={styles.badgeIconGreen}>
                      <CheckCircle2 size={20} color="#16a34a" />
                    </div>
                    <div>
                      <p style={styles.badgeLabel}>Placement Rate</p>
                      <p style={styles.badgeValue}>94%</p>
                    </div>
                  </div>
                </div>

                <div style={styles.floatingBadge2}>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "12px" }}
                  >
                    <div style={styles.badgeIconBlue}>
                      <Users size={20} color="#2563eb" />
                    </div>
                    <div>
                      <p style={styles.badgeLabel}>Active Students</p>
                      <p style={styles.badgeValue}>1,250+</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* 2) FEATURES GRID */}
      <div
        className="position-relative overflow-hidden"
        style={styles.featuresSection}
      >
        <div style={styles.featuresBgDeco1}></div>
        <div style={styles.featuresBgDeco2}></div>

        <div className="container position-relative z-10">
          <div className="text-center" style={{ marginBottom: "4rem" }}>
            <span style={styles.featuresEyebrow}>Why EduConnectPro?</span>
            <h2 className="featuresTitle" style={styles.featuresTitle}>
              The Ultimate Upgrade for <br />
              <span style={styles.gradientText}>Placement Success.</span>
            </h2>
            <p className="featuresDesc" style={styles.featuresDesc}>
              A comprehensive suite of tools designed specifically to modernize
              placement cells and improve student outcomes.
            </p>
          </div>

          <div className="row g-4">
            {features.map((f, i) => (
              <div key={i} className="col-12 col-md-6 col-lg-4">
                <div
                  className="h-100 position-relative bg-white feature-card-hover"
                  style={styles.featureCard}
                >
                  <div className="feature-card-bar" />
                  <div className="position-relative" style={{ zIndex: 10 }}>
                    <div
                      className="feature-card-icon-hover"
                      style={styles.featureCardIconWrapper}
                    >
                      <div style={{ color: "#FF5F14" }}>{f.icon}</div>
                    </div>
                    <h3
                      className="feature-card-title-hover"
                      style={styles.featureCardTitle}
                    >
                      {f.title}
                    </h3>
                    <p style={styles.featureCardDesc}>{f.desc}</p>
                  </div>
                </div>
              </div>
            ))}

            <style>{`
  .feature-card-hover {
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  }
  .feature-card-hover:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 25px -5px rgba(255, 95, 20, 0.1) !important;
    border-color: #ffedd5 !important;
  }

  .feature-card-bar {
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 100%;
    background-color: #FF5F14;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  .feature-card-hover:hover .feature-card-bar {
    transform: translateX(0);
  }

  .feature-card-icon-hover {
    transition: transform 0.3s ease;
  }
  .feature-card-hover:hover .feature-card-icon-hover {
    transform: scale(1.1);
  }

  .feature-card-title-hover {
    transition: color 0.3s ease;
  }
  .feature-card-hover:hover .feature-card-title-hover {
    color: #FF5F14 !important;
  }
`}</style>
          </div>
        </div>
      </div>

      {/* 2.5) STUDENT BENEFITS SECTION */}
      <div className="bg-white overflow-hidden" style={{ padding: "6rem 0" }}>
        <div className="container">
          <div className="row align-items-center" style={{ gap: "4rem 0" }}>
            <div className="col-12 col-lg-6">
              <span style={styles.benefitsEyebrow}>Student Experience</span>
              <h2 className="featuresTitle" style={styles.benefitsTitle}>
                What's in it for your{" "}
                <span style={{ color: "#2563eb" }}>Students?</span>
              </h2>
              <p style={styles.benefitsDesc}>
                It's not just a tool for the TPO. It's a career companion for
                every student on campus.
              </p>

              <div className="d-flex flex-column" style={{ gap: "2rem" }}>
                <div className="d-flex" style={{ gap: "1rem" }}>
                  <div style={styles.benefitIconBox}>
                    <Zap color="#2563eb" size={24} />
                  </div>
                  <div>
                    <h4 style={styles.benefitItemTitle}>Direct Job Updates</h4>
                    <p style={styles.benefitItemDesc}>
                      Students see job updates shared by the college immediately
                      on their mobile app.
                    </p>
                  </div>
                </div>

                <div className="d-flex" style={{ gap: "1rem" }}>
                  <div style={styles.benefitIconBox}>
                    <Clock color="#2563eb" size={24} />
                  </div>
                  <div>
                    <h4 style={styles.benefitItemTitle}>
                      Faster Drive Response
                    </h4>
                    <p style={styles.benefitItemDesc}>
                      Streamlined application process allows students to reply
                      to Campus Drives within 48 hours.
                    </p>
                  </div>
                </div>

                <div className="d-flex" style={{ gap: "1rem" }}>
                  <div style={styles.benefitIconBox}>
                    <Rocket color="#2563eb" size={24} />
                  </div>
                  <div>
                    <h4 style={styles.benefitItemTitle}>Startup Grants</h4>
                    <p style={styles.benefitItemDesc}>
                      Exclusive access to explore and apply for government and
                      private startup grants.
                    </p>
                  </div>
                </div>

                <div className="d-flex" style={{ gap: "1rem" }}>
                  <div style={styles.benefitIconBox}>
                    <BookOpen color="#2563eb" size={24} />
                  </div>
                  <div>
                    <h4 style={styles.benefitItemTitle}>Interview Materials</h4>
                    <p style={styles.benefitItemDesc}>
                      Access to a rich library of interview preparation
                      materials and past papers.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6 text-center">
              <div
                className="mx-auto position-relative mobile"
                style={styles.mockupContainer}
              >
                <div style={styles.mockupSideBtn1}></div>
                <div style={styles.mockupSideBtn2}></div>
                <div style={styles.mockupSideBtn3}></div>
                <div style={styles.mockupSideBtn4}></div>

                <div style={styles.mockupInner}>
                  <div style={styles.mockupHeader}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Menu size={20} />
                      <span style={{ fontWeight: "bold" }}>Freshment</span>
                      <Users size={20} />
                    </div>
                    <div style={styles.mockupSubtext}>
                      Placement Cell Updates
                    </div>
                    <div style={{ fontWeight: "bold", fontSize: "1.125rem" }}>
                      College Feed
                    </div>
                  </div>

                  <div style={styles.mockupBody}>
                    <div style={styles.mockupCard}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div style={styles.mockupCardBadgeOrange}>
                          New Drive
                        </div>
                        <span style={{ fontSize: "10px", color: "#9ca3af" }}>
                          2h ago
                        </span>
                      </div>
                      <h4
                        style={{
                          fontWeight: "bold",
                          color: "#111827",
                          fontSize: "0.875rem",
                          margin: 0,
                        }}
                      >
                        Software Engineer Trainee
                      </h4>
                      <p
                        style={{
                          fontSize: "0.75rem",
                          color: "#6b7280",
                          margin: "2px 0 0 0",
                        }}
                      >
                        TechCorp Solutions • Bangalore
                      </p>
                      <div className="d-flex mt-2" style={{ gap: "8px" }}>
                        <span style={styles.mockupPill}>₹6-8 LPA</span>
                        <span style={styles.mockupPill}>B.Tech</span>
                      </div>
                      <button style={styles.mockupBtn}>Apply Now</button>
                    </div>

                    <div style={styles.mockupCardOpq}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div style={styles.mockupCardBadgePurple}>
                          Grant Info
                        </div>
                        <span style={{ fontSize: "10px", color: "#9ca3af" }}>
                          5h ago
                        </span>
                      </div>
                      <h4
                        style={{
                          fontWeight: "bold",
                          color: "#111827",
                          fontSize: "0.875rem",
                          margin: 0,
                        }}
                      >
                        Startup India Seed Fund
                      </h4>
                      <p
                        style={{
                          fontSize: "0.75rem",
                          color: "#6b7280",
                          margin: "2px 0 0 0",
                        }}
                      >
                        Government of India
                      </p>
                      <p
                        style={{
                          fontSize: "10px",
                          color: "#9ca3af",
                          marginTop: "4px",
                          marginBottom: 0,
                        }}
                      >
                        Applications open for student startups. Get up to ₹20
                        Lakhs funding...
                      </p>
                    </div>

                    <div style={styles.mockupFab}>
                      <Plus size={24} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3) WORKFLOW */}
      <div className="bg-white" style={{ padding: "6rem 0" }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: "4rem" }}>
            <h2
              style={{
                fontSize: "1.875rem",
                fontWeight: "bold",
                color: "#111827",
                margin: 0,
              }}
            >
              Placements Made Simple
            </h2>
            <p
              style={{
                color: "#6b7280",
                marginTop: "0.5rem",
                fontSize: "1rem",
              }}
            >
              From onboarding to offer letter in 4 steps
            </p>
          </div>

          <div className="row g-4 position-relative">
            <div
              className="d-none d-md-block position-absolute"
              style={styles.workflowLine}
            ></div>
            {steps.map((item, idx) => (
              <div key={idx} className="col-12  col-md-3">
                <div
                  className="bg-white card-hover h-100"
                  style={styles.workflowCard}
                >
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ ...styles.workflowStepCircle, ...item.colorStyle }}
                  >
                    {item.step}
                  </div>
                  <h3 style={styles.workflowTitle}>{item.title}</h3>
                  <p style={styles.workflowDesc}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 4) PRICING - COOL UI */}
      <div
        className="position-relative overflow-hidden"
        style={styles.pricingSection}
      >
        <div style={styles.pricingBgDeco1}></div>
        <div style={styles.pricingBgDeco2}></div>

        <div className="container position-relative z-10">
          <div className="text-center" style={{ marginBottom: "4rem" }}>
            <h2 className="featuresTitle" style={styles.pricingTitle}>
              Simple, Transparent Pricing
            </h2>
            <p style={styles.pricingDesc}>
              Everything you need to supercharge your placements.
            </p>
          </div>

          <div className="mx-auto" style={{ maxWidth: "32rem" }}>
            <div className="bg-white mobile" style={styles.pricingCard}>
              <div style={styles.pricingHeaderBar}>
                <p
                  style={{
                    color: "white",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    margin: 0,
                  }}
                >
                  Most Popular for Colleges
                </p>
              </div>
              <div className="text-center" style={{ padding: "2rem" }}>
                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: "#111827",
                    margin: "0 0 0.5rem 0",
                  }}
                >
                  Placement Accelerator
                </h3>
                <div
                  className="d-flex justify-content-center align-items-baseline"
                  style={{ gap: "4px", margin: "1.5rem 0" }}
                >
                  <span
                    style={{
                      fontSize: "3rem",
                      fontWeight: "900",
                      color: "#111827",
                    }}
                  >
                    ₹15,000
                  </span>
                  <span style={{ color: "#6b7280", fontWeight: "500" }}>
                    / 6 months
                  </span>
                </div>

                <div
                  className="d-flex justify-content-center"
                  style={{ marginBottom: "2rem" }}
                >
                  <span style={styles.pricingLimitBadge}>
                    Up to 100 Students
                  </span>
                </div>

                <ul
                  className="text-start list-unstyled"
                  style={{ marginBottom: "2rem", padding: "0 1rem" }}
                >
                  {[
                    "300+ Verified HR Leads / Month",
                    "ATS Resume Builder Live Session",
                    "Connect HR to Schedule Off campus Drive",
                    "Track Student Progress and Application Matrix",
                    "Centralized Placement Tracking Dashboard",
                  ].map((ft, fi) => (
                    <li
                      key={fi}
                      className="d-flex align-items-start"
                      style={{
                        gap: "12px",
                        marginBottom: "1rem",
                        color: "#374151",
                        fontWeight: "500",
                      }}
                    >
                      <div style={styles.pricingCheckWrapper}>
                        <CheckCircle2 size={16} color="#16a34a" />
                      </div>
                      <span>{ft}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={scrollToForm}
                  className="orangeBtn"
                  style={styles.pricingBtn}
                >
                  Start Free Trial <ArrowRight size={20} />
                </button>
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "#9ca3af",
                    marginTop: "1rem",
                    marginBottom: 0,
                  }}
                >
                  No credit card required • Cancel anytime
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 5) TESTIMONIALS */}
      <div className="bg-white" style={{ padding: "6rem 0" }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: "4rem" }}>
            <h2
              style={{
                fontSize: "1.875rem",
                fontWeight: "bold",
                color: "#111827",
                margin: 0,
              }}
            >
              Trusted by Institutions
            </h2>
            <p
              style={{
                color: "#6b7280",
                marginTop: "0.5rem",
                fontSize: "1rem",
              }}
            >
              See what placement officers are saying
            </p>
          </div>

          <div
            className="container position-relative testimonial-viewport"
            style={styles.testimonialViewport}
          >
            {testimonialsLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : testimonials.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">
                  No testimonials available at the moment.
                </p>
              </div>
            ) : (
              <div
                className="testimonial-track"
                style={{
                  display: "flex",
                  gap: "1.1rem",
                  transform: `translateX(-${testimonialIndex * (100 / cardsPerView)}%)`,
                  transition: isTransitioning ? "transform 0.6s ease" : "none",
                }}
              >
                {extendedTestimonials.map((t, i) => (
                  <div
                    key={i}
                    style={{ flex: `0 0 calc(${100 / cardsPerView}% - 1rem)` }}
                  >
                    <div
                      className="h-100 position-relative"
                      style={styles.testimonialCard}
                    >
                      <div style={{ color: "#FF5F14", marginBottom: "1rem" }}>
                        <Quote size={32} style={{ opacity: 0.4 }} />
                      </div>
                      <p style={styles.testimonialQuote}>"{t.quote}"</p>
                      <div>
                        <p
                          style={{
                            fontWeight: "bold",
                            color: "#111827",
                            margin: 0,
                            fontSize: "16px",
                          }}
                        >
                          {t.author}
                        </p>
                        <p
                          style={{
                            fontSize: "0.75rem",
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            margin: "2px 0 0 0",
                          }}
                        >
                          {t.role}
                        </p>
                        <p
                          style={{
                            fontSize: "0.875rem",
                            color: "#FF5F14",
                            fontWeight: "500",
                            marginTop: "4px",
                            margin: 0,
                          }}
                        >
                          {t.college}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 6) REQUEST DEMO FORM */}
      <div id="request-demo" style={styles.formSection}>
        <div className="container">
          <div className="row align-items-start" style={{ gap: "4rem 0" }}>
            <div className="col-12 col-lg-6">
              <div
                className="d-inline-flex align-items-center"
                style={styles.formEyebrow}
              >
                <MessageSquare size={20} />
                Enterprise Solutions
              </div>
              <h2 className="formTitle" style={styles.formTitle}>
                Partner with Freshment
              </h2>
              <p className="formDesc" style={styles.formDesc}>
                Have more than 100 students? We offer custom enterprise plans
                with dedicated support, API access, and advanced integrations.
                Fill in the details below to schedule a walkthrough.
              </p>

              <div className="d-flex flex-column" style={{ gap: "1.5rem" }}>
                <div className="d-flex" style={{ gap: "1rem" }}>
                  <div style={styles.formIconBox}>
                    <Users size={24} color="#FF5F14" />
                  </div>
                  <div>
                    <h4 style={styles.formFeatureTitle}>
                      Student Data Management
                    </h4>
                    <p
                      className="formFeatureDesc"
                      style={styles.formFeatureDesc}
                    >
                      Centralized database for all student profiles, resumes,
                      and academic records.
                    </p>
                  </div>
                </div>
                <div className="d-flex" style={{ gap: "1rem" }}>
                  <div style={styles.formIconBox}>
                    <Building2 size={24} color="#FF5F14" />
                  </div>
                  <div>
                    <h4 style={styles.formFeatureTitle}>Company Visibility</h4>
                    <p
                      className="formFeatureDesc"
                      style={styles.formFeatureDesc}
                    >
                      Showcase your institute to 5000+ top recruiters actively
                      hiring.
                    </p>
                  </div>
                </div>
                <div className="d-flex" style={{ gap: "1rem" }}>
                  <div style={styles.formIconBox}>
                    <FileText size={24} color="#FF5F14" />
                  </div>
                  <div>
                    <h4 style={styles.formFeatureTitle}>Placement Analytics</h4>
                    <p
                      className="formFeatureDesc"
                      style={styles.formFeatureDesc}
                    >
                      Real-time dashboards to track placement percentage,
                      average package, and more.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div
                className="position-relative overflow-hidden bg-white"
                style={styles.formContainer}
              >
                <div style={styles.formCornerDeco}></div>

                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: "#0f172a",
                    marginBottom: "0.5rem",
                    position: "relative",
                    zIndex: 10,
                  }}
                >
                  Request a Free Demo
                </h3>
                <p
                  style={{
                    color: "#64748b",
                    marginBottom: "2rem",
                    position: "relative",
                    zIndex: 10,
                  }}
                >
                  Get a callback from our sales team within 24 hours.
                </p>

                <form
                  className="position-relative z-10"
                  onSubmit={(e) => {
                    e.preventDefault();
                    alert("Request sent successfully!");
                  }}
                >
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label style={styles.inputLabel}>Your Name</label>
                      <input
                        type="text"
                        required
                        style={styles.inputField}
                        placeholder="Full Name"
                      />
                    </div>
                    <div className="col-6">
                      <label style={styles.inputLabel}>Phone</label>
                      <input
                        type="tel"
                        required
                        style={styles.inputField}
                        placeholder="+91"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label style={styles.inputLabel}>Official Email</label>
                    <input
                      type="email"
                      required
                      style={styles.inputField}
                      placeholder="tpo@college.edu.in"
                    />
                  </div>

                  <div className="mb-3">
                    <label style={styles.inputLabel}>
                      College / Institute Name
                    </label>
                    <input
                      type="text"
                      required
                      style={styles.inputField}
                      placeholder="Institute Name"
                    />
                  </div>

                  <div className="mb-4">
                    <label style={styles.inputLabel}>Designation</label>
                    <select style={styles.inputField}>
                      <option>Placement Officer (TPO)</option>
                      <option>Principal / Director</option>
                      <option>HOD / Faculty</option>
                      <option>Student Coordinator</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="orangeBtn"
                    style={styles.submitBtn}
                  >
                    Submit Request <ArrowRight size={20} />
                  </button>

                  <p
                    className="text-center"
                    style={{
                      fontSize: "0.75rem",
                      color: "#94a3b8",
                      marginTop: "1rem",
                      marginBottom: 0,
                    }}
                  >
                    No credit card required for demo request.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 7) FINAL CTA */}
      <div
        className="position-relative overflow-hidden"
        style={styles.ctaSection}
      >
        <div style={styles.ctaBgGlow}></div>

        <div
          className="mx-auto position-relative z-10"
          style={{ maxWidth: "60rem", width: "96%" }}
        >
          <div className="mx-auto position-relative" style={styles.ctaCard}>
            <div style={styles.ctaCardGrid}></div>
            <div style={styles.ctaCardGlow1}></div>
            <div style={styles.ctaCardGlow2}></div>

            <div
              className="position-relative z-10 text-center mx-auto"
              style={{ maxWidth: "48rem" }}
            >
              <div
                className="d-inline-flex font-sans align-items-center"
                style={styles.ctaBadge}
              >
                <Sparkles size={12} color="#FF5F14" />
                <span>Enterprise SaaS Platform</span>
              </div>

              <h2 className="font-display ctaTitle" style={styles.ctaTitle}>
                Ready to Accelerate Your <br className="d-none d-sm-block" />
                <span className="font-display" style={styles.ctaGradientText}>
                  Placement Success?
                </span>
              </h2>

              <p className="font-sans ctaDesc" style={styles.ctaDesc}>
                Equip your campus placement cell with unified, modern software.
                Seamless candidate matching, automated communications, and live
                pipeline metrics await.
              </p>

              <div
                className="d-flex flex-column flex-sm-row justify-content-center align-items-center"
                style={{ gap: "1rem", marginTop: "2rem" }}
              >
                <button
                  onClick={scrollToForm}
                  className="orangeBtn"
                  style={styles.ctaPrimaryBtn}
                >
                  <span>Schedule Demo</span>
                  <Calendar size={16} />
                </button>
                <button
                  className="learnButton"
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                  style={styles.ctaSecondaryBtn}
                >
                  <span>Learn More</span>
                  <ArrowRight size={16} />
                </button>
              </div>

              <div
                className="d-flex flex-wrap ctaTrustRow justify-content-center align-items-center"
                style={styles.ctaTrustRow}
              >
                <span
                  className="d-flex align-items-center font-mono"
                  style={{ gap: "6px" }}
                >
                  <ShieldCheck size={14} color="#10b981" />
                  No credit card required
                </span>
                <span
                  className="d-none d-sm-inline"
                  style={{ color: "rgba(255,255,255,0.1)" }}
                >
                  •
                </span>
                <span
                  className="d-flex align-items-center font-mono"
                  style={{ gap: "6px" }}
                >
                  <Users size={14} color="#FF5F14" />
                  Active across 40+ Universities
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>
        {`
 
  .animateBtn:hover{
   transform: translateY(-3px);
  }
   .secondaryButton:hover{
   background-color: oklch(98.5% 0.002 247.839) !important;
  }
   .learnButton:hover{
  background-color: rgb(255 255 255 / 8%) !important;
   }

   .mobile:hover{
              transform: scale(1.04);
   }
 @media (max-width:1180px)
 {
         .heroTitle{font-size:3rem !important;}
 }
   @media (max-width:640px)
   {
          .ctaTitle{font-size:2rem !important;}
          .ctaDesc{font-size:12px !important;}
          .featuresTitle{font-size:2.5rem !important;}
   }
   @media (max-width:540px)
   {
          .ctaTrustRow{font-size:8px !important;}
           .heroDesc{font-size:1.15rem !important;}
   }
   @media (max-width:480px)
   {
          .ctaDesc{font-size:10px !important;}
          .formTitle{font-size: 29px !important;}
          .formDesc{font-size: 12px !important;}
          .formFeatureDesc{font-size: 11px !important;}
          .featuresDesc{font-size: 13px !important;}          
          .featuresTitle{font-size:28px !important;}
           .heroTitle{font-size:2.5rem !important;}   
           
           .heroDesc{font-size:1rem !important;}      
   }

 
`}
      </style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    fontFamily: "system-ui, -apple-system, sans-serif",
    color: "#0f172a",
  },
  testimonialViewport: {
    overflow: "hidden",
  },
  navHeader: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderBottom: "1px solid #e2e8f0",
    height: "4rem",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    color: "#475569",
    fontWeight: "bold",
    fontSize: "0.875rem",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  logoText: {
    fontWeight: "bold",
    fontSize: "1.25rem",
    letterSpacing: "-0.025em",
  },
  demoButtonSm: {
    backgroundColor: "#FF5F14",
    color: "white",
    padding: "0.5rem 1.25rem",
    borderRadius: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: "bold",
    border: "none",
    boxShadow: "0 4px 6px -1px rgba(255, 95, 20, 0.2)",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  heroSection: {
    paddingTop: "5rem",
    paddingBottom: "5rem",
  },
  heroBgBlob: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "50%",
    height: "100%",
    // backgroundColor: "rgba(255, 237, 213, 0.5)",
    transform: "skewX(-12deg)",
    transformOrigin: "top right",
    zIndex: 0,
  },
  heroEyebrow: {
    padding: "0.25rem 0.75rem",
    borderRadius: "9999px",
    backgroundColor: "#ffedd5",
    color: "#FF5F14",
    fontSize: "0.75rem",
    fontWeight: "bold",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    marginBottom: "1.5rem",
  },
  heroEyebrowDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#FF5F14",
    marginRight: "8px",
  },
  heroTitle: {
    fontSize: "3.5rem",
    fontWeight: "900",
    color: "#111827",
    letterSpacing: "-0.025em",
    marginBottom: "1.5rem",
    lineHeight: 1.1,
  },
  heroDesc: {
    fontSize: "1.25rem",
    color: "#4b5563",
    marginBottom: "2rem",
    lineHeight: 1.625,
    maxWidth: "32rem",
  },
  primaryButtonLg: {
    backgroundColor: "#FF5F14",
    color: "white",
    fontWeight: "bold",
    padding: "1rem 2rem",
    borderRadius: "0.75rem",
    boxShadow: "0 10px 15px -3px rgba(249, 115, 22, 0.2)",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    cursor: "pointer",
    fontSize: "1rem",
    transition: "transform 0.2s",
  },
  secondaryButtonLg: {
    backgroundColor: "white",
    color: "#374151",
    fontWeight: "bold",
    padding: "1rem 2rem",
    borderRadius: "0.75rem",
    border: "1px solid #d1d5db",
    cursor: "pointer",
    fontSize: "1rem",
    transition: "background-color 0.2s",
  },
  heroTrust: {
    fontSize: "0.875rem",
    color: "#6b7280",
  },
  heroImageWrapper: {
    width: "100%",
    maxWidth: "28rem",
    aspectRatio: "1/1",
    background: "linear-gradient(to bottom right, #ffedd5, white)",
    borderRadius: "50%",
    boxShadow: "0 25px 50px -12px rgba(249, 115, 22, 0.1)",
    border: "1px solid white",
  },
  heroImageCircle: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: "1px dashed #fed7aa",
    animation: "spin 20s linear infinite",
  },
  floatingBadge1: {
    position: "absolute",
    top: "2.5rem",
    right: "-1rem",
    backgroundColor: "white",
    padding: "1rem",
    borderRadius: "0.75rem",
    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
    borderLeft: "4px solid #FF5F14",
    zIndex: 20,
  },
  floatingBadge2: {
    position: "absolute",
    bottom: "2.5rem",
    left: "-1rem",
    backgroundColor: "white",
    padding: "1rem",
    borderRadius: "0.75rem",
    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
    borderLeft: "4px solid #3b82f6",
    zIndex: 20,
  },
  badgeIconGreen: {
    backgroundColor: "#dcfce7",
    padding: "0.5rem",
    borderRadius: "50%",
  },
  badgeIconBlue: {
    backgroundColor: "#dbeafe",
    padding: "0.5rem",
    borderRadius: "50%",
  },
  badgeLabel: {
    fontSize: "0.75rem",
    color: "#6b7280",
    fontWeight: "bold",
    textTransform: "uppercase",
    margin: 0,
  },
  badgeValue: {
    fontWeight: "bold",
    color: "#111827",
    fontSize: "1.125rem",
    margin: 0,
  },
  featuresSection: {
    padding: "6rem 0",
    backgroundColor: "#f9fafb",
  },
  featuresBgDeco1: {
    position: "absolute",
    top: "-6rem",
    right: "-6rem",
    width: "24rem",
    height: "24rem",
    backgroundColor: "#ffedd5",
    borderRadius: "50%",
    filter: "blur(80px)",
    pointerEvents: "none",
  },
  featuresBgDeco2: {
    position: "absolute",
    bottom: "-6rem",
    left: "-6rem",
    width: "24rem",
    height: "24rem",
    backgroundColor: "#eff6ff",
    borderRadius: "50%",
    filter: "blur(80px)",
    pointerEvents: "none",
  },
  featuresEyebrow: {
    color: "#FF5F14",
    fontWeight: "800",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontSize: "1rem",
    backgroundColor: "#fff7ed",
    padding: "0.25rem 1rem",
    borderRadius: "9999px",
    display: "inline-block",
    marginBottom: "1rem",
  },
  featuresTitle: {
    fontSize: "3.5rem",
    fontWeight: "900",
    color: "#111827",
    letterSpacing: "-0.025em",
    marginBottom: "1.5rem",
    lineHeight: 1,
  },
  gradientText: {
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
    backgroundImage: "linear-gradient(to right, #FF5F14, #ea580c)",
  },
  featuresDesc: {
    fontSize: "1.125rem",
    color: "#4b5563",
    maxWidth: "42rem",
    margin: "0 auto",
  },
  featureCard: {
    padding: "2rem",
    borderRadius: "1rem",
    border: "1px solid #f3f4f6",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
    transition: "transform 0.3s, box-shadow 0.3s",
    cursor: "default",
  },
  featureCardIconWrapper: {
    width: "3.5rem",
    height: "3.5rem",
    backgroundColor: "#fff7ed",
    borderRadius: "0.75rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "1.5rem",
  },
  featureCardTitle: {
    fontSize: "1.25rem",
    fontWeight: "bold",
    color: "#111827",
    marginBottom: "0.75rem",
  },
  featureCardDesc: {
    color: "#4b5563",
    fontSize: "0.875rem",
    lineHeight: 1.625,
    margin: 0,
  },
  benefitsEyebrow: {
    color: "#2563eb",
    fontWeight: "bold",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    fontSize: "0.875rem",
    display: "block",
    marginBottom: "0.5rem",
  },
  benefitsTitle: {
    fontSize: "2.25rem",
    fontWeight: "800",
    color: "#111827",
    marginBottom: "1.5rem",
  },
  benefitsDesc: {
    fontSize: "1.125rem",
    color: "#4b5563",
    marginBottom: "2.5rem",
  },
  benefitIconBox: {
    backgroundColor: "#eff6ff",
    padding: "0.75rem",
    borderRadius: "0.75rem",
    height: "fit-content",
  },
  benefitItemTitle: {
    fontSize: "1.25rem",
    fontWeight: "bold",
    color: "#111827",
    margin: "0 0 0.25rem 0",
  },
  benefitItemDesc: {
    color: "#4b5563",
    margin: 0,
  },
  mockupContainer: {
    width: "300px",
    height: "600px",
    backgroundColor: "#1e293b",
    border: "14px solid #1e293b",
    borderRadius: "2.5rem",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.5s",
  },
  mockupSideBtn1: {
    width: "3px",
    height: "32px",
    backgroundColor: "#1e293b",
    position: "absolute",
    left: "-17px",
    top: "72px",
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
  },
  mockupSideBtn2: {
    width: "3px",
    height: "46px",
    backgroundColor: "#1e293b",
    position: "absolute",
    left: "-17px",
    top: "124px",
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
  },
  mockupSideBtn3: {
    width: "3px",
    height: "46px",
    backgroundColor: "#1e293b",
    position: "absolute",
    left: "-17px",
    top: "178px",
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
  },
  mockupSideBtn4: {
    width: "3px",
    height: "64px",
    backgroundColor: "#1e293b",
    position: "absolute",
    right: "-17px",
    top: "142px",
    borderTopRightRadius: "8px",
    borderBottomRightRadius: "8px",
  },
  mockupInner: {
    width: "100%",
    height: "100%",
    backgroundColor: "white",
    borderRadius: "2rem",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  mockupHeader: {
    backgroundColor: "#2563eb",
    padding: "2rem 1rem 1rem 1rem",
    color: "white",
  },
  mockupSubtext: {
    fontSize: "0.75rem",
    opacity: 0.8,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  mockupBody: {
    backgroundColor: "#f9fafb",
    flex: 1,
    padding: "1rem",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    position: "relative",
  },
  mockupCard: {
    backgroundColor: "white",
    padding: "0.75rem",
    borderRadius: "0.75rem",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
    border: "1px solid #f3f4f6",
    textAlign: "left",
  },
  mockupCardOpq: {
    backgroundColor: "white",
    padding: "0.75rem",
    borderRadius: "0.75rem",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
    border: "1px solid #f3f4f6",
    textAlign: "left",
    opacity: 0.9,
  },
  mockupCardBadgeOrange: {
    backgroundColor: "#ffedd5",
    color: "#FF5F14",
    fontSize: "10px",
    fontWeight: "bold",
    padding: "2px 8px",
    borderRadius: "4px",
  },
  mockupCardBadgePurple: {
    backgroundColor: "#f3e8ff",
    color: "#9333ea",
    fontSize: "10px",
    fontWeight: "bold",
    padding: "2px 8px",
    borderRadius: "4px",
  },
  mockupPill: {
    fontSize: "10px",
    backgroundColor: "#f3f4f6",
    padding: "4px 8px",
    borderRadius: "4px",
    color: "#4b5563",
  },
  mockupBtn: {
    width: "100%",
    marginTop: "0.75rem",
    backgroundColor: "#2563eb",
    color: "white",
    fontSize: "0.75rem",
    fontWeight: "bold",
    padding: "0.5rem",
    borderRadius: "0.5rem",
    border: "none",
  },
  mockupFab: {
    position: "absolute",
    bottom: "1rem",
    right: "1rem",
    backgroundColor: "#2563eb",
    width: "3rem",
    height: "3rem",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
    zIndex: 10,
  },
  workflowLine: {
    top: "2rem",
    left: "0",
    width: "100%",
    height: "2px",
    backgroundColor: "#f3f4f6",
    zIndex: 0,
  },
  workflowCard: {
    padding: "1.5rem",
    borderRadius: "1rem",
    border: "1px solid #e5e7eb",
    position: "relative",
    zIndex: 10,
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  },
  workflowStepCircle: {
    width: "3rem",
    height: "3rem",
    borderRadius: "50%",
    fontWeight: "bold",
    fontSize: "1.125rem",
    margin: "0 0 1.5rem",
  },
  stepOrange: { backgroundColor: "#fff7ed", color: "#FF5F14" },
  stepBlue: { backgroundColor: "#eff6ff", color: "#1d4ed8" },
  stepPurple: { backgroundColor: "#faf5ff", color: "#7e22ce" },
  stepGreen: { backgroundColor: "#f0fdf4", color: "#15803d" },
  workflowTitle: {
    fontSize: "1.05rem",
    fontWeight: "bold",
    color: "#111827",
    marginBottom: "0.7rem",
  },
  workflowDesc: {
    color: "#6b7280",
    fontSize: "0.875rem",
    lineHeight: 1.625,
    margin: 0,
  },
  pricingSection: {
    padding: "6rem 0",
    backgroundColor: "#0f172a",
  },
  pricingBgDeco1: {
    position: "absolute",
    top: 0,
    right: 0,
    marginRight: "-5rem",
    marginTop: "-5rem",
    width: "24rem",
    height: "24rem",
    backgroundColor: "#FF5F14",
    borderRadius: "50%",
    filter: "blur(128px)",
    opacity: 0.2,
  },
  pricingBgDeco2: {
    position: "absolute",
    bottom: 0,
    left: 0,
    marginLeft: "-5rem",
    marginBottom: "-5rem",
    width: "20rem",
    height: "20rem",
    backgroundColor: "#2563eb",
    borderRadius: "50%",
    filter: "blur(128px)",
    opacity: 0.2,
  },
  pricingTitle: {
    fontSize: "2.25rem",
    fontWeight: "bold",
    color: "white",
    marginBottom: "1rem",
  },
  pricingDesc: {
    color: "#9ca3af",
    fontSize: "1.125rem",
  },
  pricingCard: {
    borderRadius: "1.5rem",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    border: "4px solid rgba(255, 95, 20, 0.2)",
    transition: "transform 0.5s",
  },
  pricingHeaderBar: {
    backgroundColor: "#FF5F14",
    padding: "0.5rem",
    textAlign: "center",
  },
  pricingLimitBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "0.25rem 0.75rem",
    borderRadius: "9999px",
    backgroundColor: "#fff7ed",
    color: "#FF5F14",
    fontSize: "0.875rem",
    fontWeight: "bold",
  },
  pricingCheckWrapper: {
    backgroundColor: "#dcfce7",
    padding: "4px",
    borderRadius: "50%",
    marginTop: "2px",
    flexShrink: 0,
  },
  pricingBtn: {
    width: "100%",
    backgroundColor: "#FF5F14",
    color: "white",
    fontWeight: "bold",
    padding: "1rem",
    borderRadius: "0.75rem",
    boxShadow: "0 10px 15px -3px rgba(249, 115, 22, 0.3)",
    border: "none",
    fontSize: "1.125rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  testimonialCard: {
    backgroundColor: "#f9fafb",
    padding: "2rem",
    borderRadius: "1rem",
    border: "1px solid #f3f4f6",
  },
  testimonialQuote: {
    color: "#374151",
    marginBottom: "1.5rem",
    fontStyle: "italic",
    fontSize: "0.875rem",
    lineHeight: 1.625,
  },
  formSection: {
    padding: "5rem 0",
    backgroundColor: "#f8fafc",
    borderTop: "1px solid #f1f5f9",
  },
  formEyebrow: {
    gap: "0.5rem",
    color: "#FF5F14",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "1rem",
  },
  formTitle: {
    fontSize: "2.25rem",
    fontWeight: "900",
    color: "#0f172a",
    marginBottom: "1.5rem",
  },
  formDesc: {
    fontSize: "1.125rem",
    color: "#475569",
    marginBottom: "2rem",
    lineHeight: 1.625,
  },
  formIconBox: {
    width: "3rem",
    height: "3rem",
    borderRadius: "1rem",
    backgroundColor: "#fff7ed",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  formFeatureTitle: {
    fontWeight: "bold",
    color: "#0f172a",
    fontSize: "1.125rem",
    margin: "0 0 0.25rem 0",
  },
  formFeatureDesc: {
    color: "#64748b",
    fontSize: "0.875rem",
    margin: 0,
  },
  formContainer: {
    borderRadius: "1.5rem",
    padding: "2.5rem",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    border: "1px solid #f1f5f9",
  },
  formCornerDeco: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "8rem",
    height: "8rem",
    backgroundColor: "rgba(255, 95, 20, 0.1)",
    borderBottomLeftRadius: "100%",
    marginRight: "-2.5rem",
    marginTop: "-2.5rem",
    zIndex: 0,
  },
  inputLabel: {
    fontSize: "0.75rem",
    fontWeight: "bold",
    color: "#64748b",
    textTransform: "uppercase",
    marginBottom: "0.375rem",
    display: "block",
  },
  inputField: {
    width: "100%",
    padding: "0.75rem",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "0.75rem",
    fontWeight: "500",
    outline: "none",
    color: "#0f172a",
    transition: "border-color 0.2s",
  },
  submitBtn: {
    width: "100%",
    padding: "1rem",
    backgroundColor: "#FF5F14",
    color: "white",
    fontWeight: "bold",
    borderRadius: "0.75rem",
    border: "none",
    boxShadow: "0 10px 15px -3px rgba(255, 95, 20, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    cursor: "pointer",
    marginTop: "0.5rem",
  },
  ctaSection: {
    padding: "6rem 0",
    borderTop: "1px solid #e2e8f0",
    backgroundColor: "rgba(248, 250, 252, 0.5)",
  },
  ctaBgGlow: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "700px",
    height: "700px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.04)",
    filter: "blur(160px)",
    pointerEvents: "none",
  },
  ctaCard: {
    backgroundColor: "#020617",
    color: "white",
    borderRadius: "1.5rem",
    padding: "3rem",
    border: "1px solid #1e293b",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    overflow: "hidden",
  },
  ctaCardGrid: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)",
    backgroundSize: "20px 20px",
    pointerEvents: "none",
  },
  ctaCardGlow1: {
    position: "absolute",
    top: "-6rem",
    right: "-6rem",
    width: "20rem",
    height: "20rem",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.15)",
    filter: "blur(80px)",
    pointerEvents: "none",
  },
  ctaCardGlow2: {
    position: "absolute",
    bottom: "-6rem",
    left: "-6rem",
    width: "20rem",
    height: "20rem",
    borderRadius: "50%",
    backgroundColor: "rgba(37, 99, 235, 0.1)",
    filter: "blur(80px)",
    pointerEvents: "none",
  },
  ctaBadge: {
    gap: "0.375rem",
    padding: "0.25rem 0.75rem",
    borderRadius: "9999px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    fontSize: "10px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#FF5F14",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
    marginBottom: "1.5rem",
  },
  ctaTitle: {
    fontSize: "3rem",
    fontWeight: "900",
    color: "white",
    letterSpacing: "-0.025em",
    lineHeight: 1.2,
    marginBottom: "1.5rem",
  },
  ctaGradientText: {
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
    backgroundImage: "linear-gradient(to right, white, #fb923c)",
  },
  ctaDesc: {
    fontSize: "1rem",
    color: "#94a3b8",
    lineHeight: 1.625,
    fontWeight: "500",
    margin: "0 auto",
    maxWidth: "36rem",
  },
  ctaPrimaryBtn: {
    padding: "1rem 2rem",
    borderRadius: "0.75rem",
    backgroundColor: "#FF5F14",
    color: "white",
    fontWeight: "900",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    cursor: "pointer",
    boxShadow: "0 10px 15px -3px rgba(255, 95, 20, 0.25)",
  },
  ctaSecondaryBtn: {
    padding: "1rem 2rem",
    borderRadius: "0.75rem",
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "white",
    fontWeight: "900",
    border: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    cursor: "pointer",
  },
  ctaTrustRow: {
    paddingTop: "2.5rem",
    marginTop: "2.5rem",
    borderTop: "1px solid rgba(255,255,255,0.05)",
    gap: "2rem",
    fontSize: "10px",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#64748b",
  },
  navArrow: {
    top: "50%",
    transform: "translateY(-50%)",
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    border: "1px solid #e2e8f0",
    backgroundColor: "#fff",
    color: "#FF5F14",
    transition: "all 0.2s ease",
    zIndex: 10,
  },
};

export default EduConnectPage;
